// Canonical button primitive. Replaces the 32+ hand-rolled
// `px-3 py-1.5 rounded-md text-[12px] font-medium bg-brand-600 ...`
// strings copy-pasted across every modal in the app.
//
// Tone:
//   primary   — brand-600 (the only "primary" in the system)
//   success   — emerald-600 (reserved for state-changing affirmative
//               actions: Resume, Activate, Approve, mark-paid)
//   danger    — red-600 (destructive: delete, archive)
//   secondary — slate ghost (cancel-style)
//   outline   — slate outline (alt secondary)
//
// Size: sm (text-[11px]) / md (text-[12px], default) / lg (text-[13px])
//
// Accessibility: standard <button> — focus-visible ring, aria-busy
// when loading, never strips the focus outline without replacement.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

type Tone = 'primary' | 'success' | 'danger' | 'secondary' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Tone;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

const TONE: Record<Tone, string> = {
  primary:   'bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 focus-visible:ring-brand-500/40',
  success:   'bg-[var(--color-success-fg)] text-white hover:bg-[var(--color-success-fg)] disabled:opacity-50 focus-visible:ring-[var(--color-success-border)]/40',
  danger:    'bg-[var(--color-danger-fg)] text-white hover:bg-[var(--color-danger-fg)] disabled:opacity-50 focus-visible:ring-[var(--color-danger-border)]/40',
  secondary: 'text-[var(--color-body)] hover:bg-[var(--color-panel)] dark:text-[var(--color-border)] dark:hover:bg-[var(--color-primary)] disabled:opacity-50 focus-visible:ring-[var(--color-muted)]/40',
  outline:   'border border-[var(--color-border)] dark:border-[var(--color-body)] text-[var(--color-body)] dark:text-[var(--color-border)] hover:bg-[var(--color-panel)] dark:hover:bg-[var(--color-primary)] disabled:opacity-50 focus-visible:ring-[var(--color-muted)]/40',
};

const SIZE: Record<Size, string> = {
  sm: 'px-2.5 py-1 text-[11px] gap-1',
  md: 'px-3 py-1.5 text-[12px] gap-1.5',
  lg: 'px-4 py-2 text-[13px] gap-2',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { tone = 'primary', size = 'md', loading, leadingIcon, trailingIcon, fullWidth, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2',
        TONE[tone],
        SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
